import { toRiderServicesPayload } from '../sections/RiderServicesSection/toRiderServicesPayload';
import type { RiderFormValues } from '../types';

const BASIC_INFO_KEYS = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'gender',
  'email',
  'mobilePhoneNumber',
  'photosAttributes',
  'category',
  'externalId',
  'invoiceGroupId',
  'isAide',
] as const satisfies readonly (keyof RiderFormValues)[];

const SERVICES_KEYS = [
  'codeWord',
  'frontSeatAuthorized',
  'boosterSeat',
  'services',
  'supportSettings',
  'riderNotes',
] as const satisfies readonly (keyof RiderFormValues)[];

type DirtyFields = Partial<Record<keyof RiderFormValues, unknown>>;

const buildShared = (values: RiderFormValues, confirmed = false) =>
  values.isSharedBillingRider
    ? {
        isSharedBillingRider: true as const,
        sharedBillingRider: { ...values.sharedBillingRider, ...(confirmed && { confirmed }) },
      }
    : { isSharedBillingRider: false as const };

export const toCreateRiderPayload = (values: RiderFormValues) => ({
  ...Object.fromEntries(BASIC_INFO_KEYS.map((k) => [k, values[k]])),
  passengersContactsAttributes: values.contacts,
  ...toRiderServicesPayload(values),
  ...buildShared(values, true),
});

export const toPatchRiderPayload = (
  values: RiderFormValues,
  dirtyFields: DirtyFields,
  confirmedSharedBilling = false,
  originalContactIds: number[] = [],
  originalIsSharedBillingRider?: boolean
) => {
  const payload: Record<string, unknown> = {};

  for (const key of BASIC_INFO_KEYS) {
    if (dirtyFields[key]) {
      payload[key] = values[key];
    }
  }

  if (dirtyFields.contacts) {
    payload.passengersContactsAttributes = values.contacts;

    const currentContactIds = new Set(values.contacts.map((c) => c.id).filter((id): id is number => id !== undefined));
    const removedContactIds = originalContactIds.filter((id) => !currentContactIds.has(id));

    if (removedContactIds.length > 0) {
      payload.passengersContactsIdsToDelete = removedContactIds;
    }
  }

  if (dirtyFields.dateOfBirth || SERVICES_KEYS.some((k) => dirtyFields[k])) {
    const { supportSettings, ...restServicesPayload } = toRiderServicesPayload(values);

    Object.assign(payload, restServicesPayload);

    if (dirtyFields.supportSettings) {
      payload.supportSettings = supportSettings;
    }
  }

  const sharedBillingToggled =
    originalIsSharedBillingRider === undefined
      ? Boolean(dirtyFields.isSharedBillingRider)
      : values.isSharedBillingRider !== originalIsSharedBillingRider;
  const sharedBillingDetailsChanged = Boolean(values.isSharedBillingRider && dirtyFields.sharedBillingRider);

  if (sharedBillingToggled || sharedBillingDetailsChanged) {
    if (!values.isSharedBillingRider) {
      Object.assign(payload, { isSharedBillingRider: false, sharedBillingRider: { inactive: true } });
    } else {
      Object.assign(payload, buildShared(values, confirmedSharedBilling));
    }
  }

  return payload;
};
